/**
 * AutoReplySettingsPage — две оси режима: когда готовить черновик и что с ним
 * делать.
 *
 * Ось ГЕНЕРАЦИИ стоит первой намеренно: она решает, появится ли черновик
 * вообще, а ось доставки — что с готовым делать. Обратный порядок предлагал бы
 * выбрать судьбу того, чего может не быть.
 *
 * Phase 9: Shadow / Бот / Ассистент + Readiness. Ось генерации и прогноз —
 * 10.09, спека `.claude/specs/selective-generation.md`.
 */

import { Spinner, Card, Badge } from '../components/ui';
import {
  useAutoReplySettings,
  useSetAutoReplyMode,
  useSetGenerationMode,
  useSetChannelGenerationMode,
  useGenerationForecast,
  useReadinessMetrics,
  type AutoReplyMode,
  type GenerationMode,
} from '../api/auto-reply';
import {
  AUTO_REPLY_MODE_INFO,
  GENERATION_MODE_INFO,
  CHANNEL_LABELS,
  type ModeInfo,
} from '../lib/mode-labels';
import { getErrorMessage } from '../lib/error-messages';

// ============================================================================
// Карточка режима — одна форма на обе оси
// ============================================================================

interface ModeCardProps {
  info: ModeInfo;
  isActive: boolean;
  isLocked?: boolean;
  lockedHint?: string;
  disabled?: boolean;
  onSelect: () => void;
  /** Строка под описанием: прогноз по фактическим данным. */
  footnote?: string | undefined;
}

function ModeCard({
  info,
  isActive,
  isLocked = false,
  lockedHint,
  disabled = false,
  onSelect,
  footnote,
}: ModeCardProps): JSX.Element {
  return (
    <button
      onClick={onSelect}
      disabled={isLocked || disabled}
      className={[
        'w-full text-left px-6 py-4 rounded-comp border transition-all',
        isActive
          ? 'border-accent-brand bg-accent-brand/5 shadow-sm'
          : isLocked
          ? 'border-border-subtle bg-surface-subtle opacity-60 cursor-not-allowed'
          : 'border-border-default bg-surface-base hover:border-accent-brand/50 cursor-pointer',
      ].join(' ')}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{info.icon}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-text-primary">{info.label}</span>
            {isActive && <Badge variant="ai">Активен</Badge>}
            {isLocked && lockedHint && (
              <span className="text-xs text-text-muted">{lockedHint}</span>
            )}
          </div>
          <p className="text-sm text-text-secondary mt-0.5">{info.description}</p>
          {footnote && <p className="text-xs text-accent-brand mt-1.5">{footnote}</p>}
        </div>
        {isActive && <div className="w-3 h-3 rounded-full bg-accent-brand" />}
      </div>
    </button>
  );
}

// ============================================================================
// Page
// ============================================================================

export function AutoReplySettingsPage(): JSX.Element {
  const { data: settings, isLoading: settingsLoading } = useAutoReplySettings();
  const { data: metrics, isLoading: metricsLoading } = useReadinessMetrics();
  const { data: forecast, error: forecastError } = useGenerationForecast();
  const setMode = useSetAutoReplyMode();
  const setGeneration = useSetGenerationMode();
  const setChannelGeneration = useSetChannelGenerationMode();

  if (settingsLoading || metricsLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-base">
        <Spinner size="lg" className="text-accent-brand" />
      </div>
    );
  }

  const currentMode = settings?.mode ?? 'shadow';
  const currentGeneration = settings?.generationMode ?? 'always';
  const readiness = metrics?.readiness ?? 0;
  const assistantAvailable = metrics?.assistantAvailable ?? false;

  /**
   * Отказ прогноза различается по причине.
   *
   * «Доступен администратору» вместо любой ошибки было бы враньём в половине
   * случаев: 500 от неподключённой службы виджета выглядел бы как вопрос прав,
   * и искали бы не там.
   */
  const статусОтказа = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } } | null)?.response?.status;

  const handleModeChange = (mode: AutoReplyMode): void => {
    if (mode === 'assistant' && !assistantAvailable) return;
    setMode.mutate(mode);
  };

  /**
   * Прогноз по фактическим данным — под каждым режимом своё число.
   *
   * Без него включение «только в начатых беседах» неотличимо от поломки:
   * черновики перестают появляться, и понять, так задумано или сломалось,
   * не по чему.
   */
  const прогноз = (mode: GenerationMode): string | undefined => {
    if (!forecast) return undefined;
    const { total, withOurReply } = forecast;
    if (mode === 'always') return `Сейчас под это правило попадают все ${total} диалогов`;
    if (mode === 'known') {
      return `Сейчас под это правило попадает ${withOurReply} из ${total} диалогов`;
    }
    return `Автоматических черновиков не будет ни в одном из ${total} диалогов`;
  };

  return (
    <div className="h-full bg-surface-base overflow-auto">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold text-text-primary">Режим работы</h1>
          <p className="text-text-secondary text-sm mt-1">
            Когда AI готовит черновик и что с ним делать дальше
          </p>
        </div>

        {/* Ось генерации */}
        <div className="mb-2">
          <h2 className="font-heading text-lg font-medium text-text-primary">
            Когда готовить черновик
          </h2>
          <p className="text-text-secondary text-sm mt-0.5 mb-3">
            Кнопка «Подготовить ответ» в диалоге работает при любом режиме
          </p>
        </div>
        <div className="space-y-3 mb-3">
          {(Object.keys(GENERATION_MODE_INFO) as GenerationMode[]).map((mode) => (
            <ModeCard
              key={mode}
              info={GENERATION_MODE_INFO[mode]}
              isActive={currentGeneration === mode}
              disabled={setGeneration.isPending}
              onSelect={() => setGeneration.mutate(mode)}
              footnote={прогноз(mode)}
            />
          ))}
        </div>

        {/* Прогноз: разбивка и честные оговорки */}
        {forecast && (
          <div className="mb-8 px-4 py-3 rounded-comp bg-surface-subtle border border-border-subtle text-xs text-text-secondary leading-relaxed">
            {/*
              Правило на канал стоит ЗДЕСЬ, рядом со своим числом, а не
              отдельным блоком: выбор «готовить всегда для виджета» осмыслен
              ровно тогда, когда видно, что под общее правило в виджете
              попадает 4 диалога из 12. Числа без выбора были подсказкой без
              кнопки, выбор без чисел — кнопкой без причины.
            */}
            <div className="mb-2 text-text-muted">
              Своё правило для канала. Число — сколько диалогов канала попадает под «только в
              начатых беседах»
            </div>
            <div className="space-y-2 mb-3">
              {(Object.keys(CHANNEL_LABELS) as Array<keyof typeof CHANNEL_LABELS>).map((канал) => {
                const своё = settings?.channelGeneration?.[канал] ?? null;
                return (
                  <div key={канал} className="flex items-center gap-2 flex-wrap">
                    <span className="text-text-primary w-20 shrink-0">
                      {CHANNEL_LABELS[канал]}
                    </span>
                    <span className="text-text-secondary w-24 shrink-0">
                      {forecast.byChannel[канал].withOurReply} из {forecast.byChannel[канал].total}
                    </span>
                    <select
                      value={своё ?? ''}
                      disabled={setChannelGeneration.isPending}
                      onChange={(e) =>
                        setChannelGeneration.mutate({
                          channel: канал,
                          // Пустая строка — снятие: правило снова берётся у организации
                          mode: (e.target.value || null) as GenerationMode | null,
                        })
                      }
                      className="px-2 py-1 rounded-comp border border-border-default bg-surface-base text-text-primary text-xs max-w-full min-w-0 cursor-pointer"
                    >
                      {/*
                        Действующее значение названо ЗДЕСЬ, в подписи опции, а не
                        подставлено в сам селект: иначе после переключения
                        организации у всех каналов появилось бы «своё» правило,
                        которого нет.
                      */}
                      <option value="">
                        Как в организации ({GENERATION_MODE_INFO[currentGeneration].label})
                      </option>
                      {(Object.keys(GENERATION_MODE_INFO) as GenerationMode[]).map((mode) => (
                        <option key={mode} value={mode}>
                          {GENERATION_MODE_INFO[mode].label}
                        </option>
                      ))}
                    </select>
                    {своё && <Badge variant="waiting">своё правило</Badge>}
                  </div>
                );
              })}
            </div>
            {setChannelGeneration.isError && (
              <div className="mb-2 text-accent-danger">
                Правило канала не изменено: {getErrorMessage(setChannelGeneration.error)}
              </div>
            )}
            {/*
              Две оговорки, и обе обязательны. Первая — про приближение:
              ответ, набранный без цитирования, движок беседой не считает.
              Вторая — про то, чем это правило НЕ является: подтверждения
              личности отправителя письма в проекте нет (проверок DKIM/SPF
              тоже), и обещать защиту от посторонних значило бы обещать
              отсутствующее. Отметило ревью безопасности 10.09.
            */}
            <div className="text-text-muted">
              Считаются диалоги, где клиент писал хотя бы раз; знакомым диалог считается,
              если мы отвечали тому, кто написал в нём последним. Наш ответ, набранный без
              цитирования письма, движок беседой не сочтёт. Это правило про экономию, а не
              защита от посторонних: адрес отправителя письма ничем не подтверждён.
            </div>
          </div>
        )}
        {forecastError && (
          <div className="mb-8 px-4 py-3 rounded-comp bg-surface-subtle border border-border-subtle text-xs text-text-muted">
            {статусОтказа(forecastError) === 403
              ? 'Прогноз по диалогам доступен администратору: он считается по всем каналам организации.'
              : `Прогноз не посчитался: ${getErrorMessage(forecastError)}`}
          </div>
        )}

        {setGeneration.isError && (
          <div className="mb-6 px-4 py-3 rounded-comp bg-accent-danger/10 border border-accent-danger/20 text-accent-danger text-sm">
            Режим генерации не изменён: {getErrorMessage(setGeneration.error)}
          </div>
        )}

        {/* Ось доставки */}
        <div className="mb-3">
          <h2 className="font-heading text-lg font-medium text-text-primary">
            Что делать с готовым черновиком
          </h2>
        </div>
        <div className="space-y-3 mb-8">
          {(Object.keys(AUTO_REPLY_MODE_INFO) as AutoReplyMode[]).map((mode) => (
            <ModeCard
              key={mode}
              info={AUTO_REPLY_MODE_INFO[mode]}
              isActive={currentMode === mode}
              isLocked={mode === 'assistant' && !assistantAvailable}
              lockedHint={`Доступен при готовности ${metrics?.assistantThreshold ?? 70}%`}
              disabled={setMode.isPending}
              onSelect={() => handleModeChange(mode)}
            />
          ))}
        </div>

        {/* Error */}
        {setMode.isError && (
          <div className="mb-6 px-4 py-3 rounded-comp bg-accent-danger/10 border border-accent-danger/20 text-accent-danger text-sm">
            Режим не изменён: {getErrorMessage(setMode.error)}
          </div>
        )}

        {/* Readiness metrics */}
        <Card className="p-6">
          <h2 className="font-heading text-lg font-medium text-text-primary mb-4">
            Готовность ассистента
          </h2>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Готовность</span>
              <span className="text-sm font-medium text-text-primary">{readiness}%</span>
            </div>
            <div className="w-full h-3 bg-surface-subtle rounded-full overflow-hidden">
              <div
                className={[
                  'h-full rounded-full transition-all duration-500',
                  readiness >= 70
                    ? 'bg-accent-success'
                    : readiness >= 40
                    ? 'bg-accent-warning'
                    : 'bg-accent-danger',
                ].join(' ')}
                style={{ width: `${Math.min(readiness, 100)}%` }}
              />
            </div>
            {!assistantAvailable && (
              <p className="text-xs text-text-muted mt-2">
                Режим "Ассистент" станет доступен при {metrics?.assistantThreshold ?? 70}%
              </p>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-comp bg-surface-subtle">
              <div className="text-2xl font-bold text-text-primary">
                {metrics?.totalProposals ?? 0}
              </div>
              <div className="text-xs text-text-secondary mt-1">Черновиков за 30 дней</div>
            </div>
            <div className="text-center p-3 rounded-comp bg-surface-subtle">
              <div className="text-2xl font-bold text-text-primary">
                {metrics?.correctionRate ?? 0}%
              </div>
              <div className="text-xs text-text-secondary mt-1">Коррекций</div>
            </div>
            <div className="text-center p-3 rounded-comp bg-surface-subtle">
              <div className="text-2xl font-bold text-text-primary">
                {metrics?.escalationRate ?? 0}%
              </div>
              <div className="text-xs text-text-secondary mt-1">Эскалаций</div>
            </div>
          </div>
        </Card>

        {/* Hint */}
        {readiness < 70 && (
          <div className="mt-6 px-4 py-3 rounded-comp bg-accent-ai/5 border border-accent-ai/20 text-sm text-text-secondary">
            Добавляйте знания в Базу знаний и работайте в Shadow-режиме — готовность будет расти
            автоматически.
          </div>
        )}
      </div>
    </div>
  );
}
